import { createContext, type ReactNode, use } from "react"

export interface ModalActivityContextValue {
  isActive: boolean
  close(): void
}

const ModalActivityContext = createContext<ModalActivityContextValue | null>(null)

interface ModalActivityProviderProps {
  value: ModalActivityContextValue
  children: ReactNode
}

/**
 * ModalActivityProvider exposes only the modal activity state that bodies need
 * (isActive flag and close handler) to keep UI concerns isolated from business data.
 */
export const ModalActivityProvider: React.FC<ModalActivityProviderProps> = ({ value, children }) => (
  <ModalActivityContext.Provider value={value}>{children}</ModalActivityContext.Provider>
)

export const useModalActivity = (): ModalActivityContextValue => {
  const context = use(ModalActivityContext)

  if (!context) {
    throw new Error("ModalActivityProvider is required to use useModalActivity")
  }

  return context
}

/**
 * useModalActivityOptional - Returns modal activity from context, or null if not wrapped in provider.
 * Useful for components that can work both with and without ModalActivityProvider.
 */
export const useModalActivityOptional = (): ModalActivityContextValue | null => {
  return use(ModalActivityContext)
}
